import { useCallback, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_TRANSFORM_STATE,
  FREE_ROTATION_MAX,
  FREE_ROTATION_MIN,
  ROTATION_STEP,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_STEP,
} from '../config';
import type { CropBox, ImageTransformState } from '../config';

type PersistedTransformInput = {
  rotation: number;
  flipHorizontal: boolean;
  cropBox: CropBox | null;
  zoom?: number;
  panX?: number;
  panY?: number;
};

export const normalizeRotation = (deg: number): number => {
  const SNAP_EPSILON = 0.5;
  const mod = ((deg % 360) + 360) % 360;
  const snappedToQuarterTurn = Math.round(mod / 90) * 90;

  if (Math.abs(mod - snappedToQuarterTurn) <= SNAP_EPSILON) {
    const snappedMod = snappedToQuarterTurn % 360;

    return snappedMod > 180 ? snappedMod - 360 : snappedMod;
  }

  return mod > 180 ? mod - 360 : mod;
};

export const getTotalRotation = (transformState: ImageTransformState): number =>
  transformState.baseRotation + transformState.freeRotation;

export const useImageTransforms = () => {
  const [transformState, setTransformState] = useState<ImageTransformState>(
    DEFAULT_TRANSFORM_STATE,
  );

  const snapshotRef = useRef<ImageTransformState>(DEFAULT_TRANSFORM_STATE);

  const saveSnapshot = useCallback(() => {
    setTransformState((current) => {
      snapshotRef.current = current;

      return current;
    });
  }, []);

  const restoreSnapshot = useCallback(() => {
    setTransformState(snapshotRef.current);
  }, []);

  const rotate90CW = useCallback(() => {
    setTransformState((prev) => ({
      ...prev,
      baseRotation: prev.baseRotation + ROTATION_STEP,
      freeRotation: 0,
    }));
  }, []);

  const rotate90CCW = useCallback(() => {
    setTransformState((prev) => ({
      ...prev,
      baseRotation: prev.baseRotation - ROTATION_STEP,
      freeRotation: 0,
    }));
  }, []);

  const setFreeRotation = useCallback((angle: number) => {
    const clampedAngle = Math.max(
      FREE_ROTATION_MIN,
      Math.min(FREE_ROTATION_MAX, angle),
    );

    setTransformState((prev) => ({
      ...prev,
      freeRotation: clampedAngle,
    }));
  }, []);

  const toggleMirror = useCallback(() => {
    setTransformState((prev) => ({
      ...prev,
      flipHorizontal: !prev.flipHorizontal,
    }));
  }, []);

  const zoomIn = useCallback(() => {
    setTransformState((prev) => ({
      ...prev,
      zoom: Math.min(ZOOM_MAX, prev.zoom + ZOOM_STEP),
    }));
  }, []);

  const zoomOut = useCallback(() => {
    setTransformState((prev) => ({
      ...prev,
      zoom: Math.max(ZOOM_MIN, prev.zoom - ZOOM_STEP),
    }));
  }, []);

  const setZoom = useCallback((level: number) => {
    setTransformState((prev) => ({
      ...prev,
      zoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, level)),
    }));
  }, []);

  const setPan = useCallback((panX: number, panY: number) => {
    setTransformState((prev) => ({
      ...prev,
      panX,
      panY,
    }));
  }, []);

  const setCropBox = useCallback((cropBox: CropBox | null) => {
    setTransformState((prev) => ({
      ...prev,
      cropBox,
    }));
  }, []);

  const resetTransforms = useCallback(() => {
    setTransformState(DEFAULT_TRANSFORM_STATE);
  }, []);

  const initializeFromPersisted = useCallback(
    (persisted: PersistedTransformInput) => {
      setTransformState({
        baseRotation: persisted.rotation,
        freeRotation: 0,
        flipHorizontal: persisted.flipHorizontal,
        cropBox: persisted.cropBox,
        zoom: persisted.zoom ?? 1,
        panX: persisted.panX ?? 0,
        panY: persisted.panY ?? 0,
      });
    },
    [],
  );

  const isDirty = useMemo(
    () =>
      transformState.baseRotation !== 0 ||
      transformState.freeRotation !== 0 ||
      transformState.flipHorizontal !== false ||
      transformState.cropBox !== null ||
      transformState.zoom !== 1 ||
      transformState.panX !== 0 ||
      transformState.panY !== 0,
    [transformState],
  );
  const totalRotation = useMemo(
    () => getTotalRotation(transformState),
    [transformState],
  );

  return {
    transformState,
    totalRotation,
    rotate90CW,
    rotate90CCW,
    setFreeRotation,
    toggleMirror,
    zoomIn,
    zoomOut,
    setZoom,
    setPan,
    setCropBox,
    resetTransforms,
    initializeFromPersisted,
    isDirty,
    saveSnapshot,
    restoreSnapshot,
  };
};
